import { JSBI, Percent, Router, SwapParameters, Trade, TradeType } from '@etcswapv2/sdk-legacy'
import { useMemo } from 'react'
import { usePublicClient, useWalletClient } from 'wagmi'
import { type Address, encodeFunctionData } from 'viem'
import { BIPS_BASE, DEFAULT_DEADLINE_FROM_NOW, INITIAL_ALLOWED_SLIPPAGE, ROUTER_ADDRESS } from '../constants'
import { routerAbi } from '../constants/abis'
import { useTransactionAdder } from '../state/transactions/hooks'
import { isAddress, shortenAddress } from '../utils'
import { useActiveWeb3React } from './index'
import useENS from './useENS'
import { getChainById } from '../wagmi'

export enum SwapCallbackState {
  INVALID,
  LOADING,
  VALID
}

interface SwapCall {
  parameters: SwapParameters
}

interface SuccessfulCall {
  call: SwapCall
  gasEstimate: bigint
}

interface FailedCall {
  call: SwapCall
  error: Error
}

type EstimatedSwapCall = SuccessfulCall | FailedCall

// Valid router function names from the ABI
type RouterFunctionName =
  | 'swapExactETCForTokens'
  | 'swapExactETCForTokensSupportingFeeOnTransferTokens'
  | 'swapExactTokensForETC'
  | 'swapExactTokensForETCSupportingFeeOnTransferTokens'
  | 'swapExactTokensForTokens'
  | 'swapExactTokensForTokensSupportingFeeOnTransferTokens'
  | 'swapETCForExactTokens'
  | 'swapTokensForExactETC'
  | 'swapTokensForExactTokens'
  | 'addLiquidity'
  | 'addLiquidityETC'
  | 'removeLiquidity'
  | 'removeLiquidityETC'

// Map ETH method names to ETC method names (ETC router uses ETC naming)
const METHOD_NAME_MAP: Record<string, RouterFunctionName> = {
  swapExactETHForTokens: 'swapExactETCForTokens',
  swapExactETHForTokensSupportingFeeOnTransferTokens: 'swapExactETCForTokensSupportingFeeOnTransferTokens',
  swapExactTokensForETH: 'swapExactTokensForETC',
  swapExactTokensForETHSupportingFeeOnTransferTokens: 'swapExactTokensForETCSupportingFeeOnTransferTokens',
  swapETHForExactTokens: 'swapETCForExactTokens',
  swapTokensForExactETH: 'swapTokensForExactETC'
}

// Get the ETC method name from the SDK's ETH naming
function getETCMethodName(methodName: string): RouterFunctionName {
  return METHOD_NAME_MAP[methodName] ?? (methodName as RouterFunctionName)
}

// Convert SDK args to viem-compatible format (JSBI to bigint, addresses to Address type)
function convertArgsForViem(args: (string | string[])[]): readonly unknown[] {
  return args.map(arg => {
    if (Array.isArray(arg)) {
      // Address array (path)
      return arg as readonly Address[]
    }
    // Check if it's a hex address
    if (arg.startsWith('0x')) {
      return arg as Address
    }
    // Numeric value - convert to bigint
    return BigInt(arg)
  })
}

/**
 * Returns the swap calls that can be used to make the trade
 * @param trade trade to execute
 * @param allowedSlippage user allowed slippage
 * @param ttl time to live in seconds from now
 * @param recipientAddressOrName
 */
function useSwapCallArguments(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  ttl: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): SwapCall[] {
  const { account, chainId } = useActiveWeb3React()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !recipient || !account || !chainId) return []

    // Calculate deadline timestamp from ttl
    const deadline = Math.floor(Date.now() / 1000) + ttl

    const swapMethods: SwapParameters[] = []

    // Generate swap call parameters
    swapMethods.push(
      Router.swapCallParameters(trade, {
        feeOnTransfer: false,
        allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
        recipient,
        deadline
      })
    )

    if (trade.tradeType === TradeType.EXACT_INPUT) {
      swapMethods.push(
        Router.swapCallParameters(trade, {
          feeOnTransfer: true,
          allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
          recipient,
          deadline
        })
      )
    }

    return swapMethods.map(parameters => ({ parameters }))
  }, [account, allowedSlippage, chainId, ttl, recipient, trade])
}

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useSwapCallback(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  ttl: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): { state: SwapCallbackState; callback: null | (() => Promise<string>); error: string | null } {
  const { account, chainId } = useActiveWeb3React()
  const publicClient = usePublicClient()
  const { data: walletClient } = useWalletClient()

  const swapCalls = useSwapCallArguments(trade, allowedSlippage, ttl, recipientAddressOrName)

  const addTransaction = useTransactionAdder()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !publicClient || !walletClient || !account || !chainId) {
      return { state: SwapCallbackState.INVALID, callback: null, error: 'Missing dependencies' }
    }
    if (!recipient) {
      if (recipientAddressOrName !== null) {
        return { state: SwapCallbackState.INVALID, callback: null, error: 'Invalid recipient' }
      } else {
        return { state: SwapCallbackState.LOADING, callback: null, error: null }
      }
    }

    const routerAddress = ROUTER_ADDRESS[chainId] as Address | undefined
    if (!routerAddress) {
      return { state: SwapCallbackState.INVALID, callback: null, error: 'Router not found for this chain' }
    }

    const chain = getChainById(chainId)
    if (!chain) {
      return { state: SwapCallbackState.INVALID, callback: null, error: 'Unsupported chain' }
    }

    return {
      state: SwapCallbackState.VALID,
      callback: async function onSwap(): Promise<string> {
        const estimatedCalls: EstimatedSwapCall[] = await Promise.all(
          swapCalls.map(async call => {
            const {
              parameters: { methodName, args, value }
            } = call

            // Convert ETH method names to ETC equivalents
            const etcMethodName = getETCMethodName(methodName)
            const viemArgs = convertArgsForViem(args)
            const valueAsBigInt = value && value !== '0' ? BigInt(value) : 0n

            // Encode the function call data for dynamic function names
            // Use type assertion for args since we're constructing them dynamically from SDK
            const data = encodeFunctionData({
              abi: routerAbi,
              functionName: etcMethodName,
              args: viemArgs as readonly unknown[]
            } as Parameters<typeof encodeFunctionData>[0])

            try {
              const gasEstimate = await publicClient.estimateGas({
                to: routerAddress,
                data,
                value: valueAsBigInt,
                account: account as Address
              })

              return { call, gasEstimate }
            } catch {
              console.debug('Gas estimate failed, trying to extract error', call)

              try {
                await publicClient.call({
                  to: routerAddress,
                  data,
                  value: valueAsBigInt,
                  account: account as Address
                })
                return { call, error: new Error('Unexpected issue with estimating the gas. Please try again.') }
              } catch (callError) {
                console.debug('Call threw error', call, callError)
                let errorMessage: string
                const reason =
                  (callError as { cause?: { reason?: string } })?.cause?.reason ||
                  (callError as Error)?.message ||
                  ''
                if (reason.includes('INSUFFICIENT_OUTPUT_AMOUNT') || reason.includes('EXCESSIVE_INPUT_AMOUNT')) {
                  errorMessage =
                    'This transaction will not succeed either due to price movement or fee on transfer. Try increasing your slippage tolerance.'
                } else {
                  errorMessage = `The transaction cannot succeed due to error: ${reason}. This is probably an issue with one of the tokens you are swapping.`
                }
                return { call, error: new Error(errorMessage) }
              }
            }
          })
        )

        // a successful estimation is a bignumber gas estimate and the next call is also a bignumber gas estimate
        const successfulEstimation = estimatedCalls.find(
          (el, ix, list): el is SuccessfulCall =>
            'gasEstimate' in el && (ix === list.length - 1 || 'gasEstimate' in list[ix + 1])
        )

        if (!successfulEstimation) {
          const errorCalls = estimatedCalls.filter((call): call is FailedCall => 'error' in call)
          if (errorCalls.length > 0) throw errorCalls[errorCalls.length - 1].error
          throw new Error('Unexpected error. Please contact support: none of the calls threw an error')
        }

        const {
          call: {
            parameters: { methodName, args, value }
          },
          gasEstimate
        } = successfulEstimation

        // Convert ETH method names to ETC equivalents
        const etcMethodName = getETCMethodName(methodName)
        const viemArgs = convertArgsForViem(args)
        const valueAsBigInt = value && value !== '0' ? BigInt(value) : 0n

        // Encode the function call data
        const data = encodeFunctionData({
          abi: routerAbi,
          functionName: etcMethodName,
          args: viemArgs as readonly unknown[]
        } as Parameters<typeof encodeFunctionData>[0])

        // Add 20% gas margin
        const gasLimit = (gasEstimate * 120n) / 100n

        try {
          // Use type assertion for sendTransaction - viem 2.x has complex blob transaction types
          // that don't apply to regular contract calls on ETC (no blob support)
          const hash = await walletClient.sendTransaction({
            to: routerAddress,
            data,
            value: valueAsBigInt,
            gas: gasLimit,
            chain,
            account: account as Address
          } as unknown as Parameters<typeof walletClient.sendTransaction>[0])

          const inputSymbol = trade.inputAmount.currency.symbol
          const outputSymbol = trade.outputAmount.currency.symbol
          const inputAmount = trade.inputAmount.toSignificant(3)
          const outputAmount = trade.outputAmount.toSignificant(3)

          const summary = `Swap ${inputAmount} ${inputSymbol} for ${outputAmount} ${outputSymbol}${
            recipient !== account
              ? ` to ${
                  recipientAddressOrName && isAddress(recipientAddressOrName)
                    ? shortenAddress(recipientAddressOrName)
                    : recipientAddressOrName
                }`
              : ''
          }`

          addTransaction({ hash }, { summary })

          return hash
        } catch (error) {
          // if the user rejected the tx, pass this along
          if (
            (error as { code?: number })?.code === 4001 ||
            (error as Error)?.message?.includes('rejected')
          ) {
            throw new Error('Transaction rejected.')
          } else {
            // otherwise, the error was unexpected and we need to convey that
            console.error(`Swap failed`, error, etcMethodName, viemArgs, valueAsBigInt)
            throw new Error(`Swap failed: ${(error as Error).message}`)
          }
        }
      },
      error: null
    }
  }, [trade, publicClient, walletClient, account, chainId, recipient, recipientAddressOrName, swapCalls, addTransaction])
}
